import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ArrowTopRightOnSquareIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  XCircleIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, SortableTableHeader, Text } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { useAuthContext } from '../../contexts/AuthContext'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import { modulesService } from '../../services/modules'
import { marketingLinksService } from '../../services/marketingLinks'
import type { EntityType } from '../../services/userLogs'
import type { MarketingLink, MarketingLinkInput, MarketingLinkKind } from '../../types/marketingLinks'
import {
  ICON_BTN_CLASS,
  ICON_BTN_DANGER_CLASS,
  PERM_LOADING,
  TEXTAREA_CLASS,
  sortRows,
} from '../Estoque/estoqueUi'

const PAGE_SIZE = 20
const PAGINATION_BTN_CLASS =
  'rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'

const LINK_ICON_BTN_CLASS =
  'inline-flex items-center justify-center rounded-md border border-primary-200 bg-primary-50 p-1.5 text-primary-600 transition-colors hover:border-primary-300 hover:bg-primary-100 hover:text-primary-700 dark:border-primary-800 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:border-primary-700 dark:hover:bg-primary-900/50'

interface LinksFilters {
  search: string
}

const EMPTY_FILTERS: LinksFilters = {
  search: '',
}

interface FormState {
  title: string
  description: string
  download_url: string
}

function emptyForm(): FormState {
  return {
    title: '',
    description: '',
    download_url: '',
  }
}

function isValidUrl(value: string): boolean {
  try {
    const url = new URL(value.trim())
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

function formatDateTimePtBr(value: string | null | undefined): string {
  if (!value) return '—'
  return new Date(value).toLocaleString('pt-BR')
}

export interface MarketingLinksScreenTexts {
  fallbackTitle: string
  fallbackDescription: string
  newButton: string
  modalCreateTitle: string
  modalEditTitle: string
  titlePlaceholder: string
  descriptionPlaceholder: string
  emptyFiltered: string
  emptyList: string
  titleRequired: string
  loadError: string
  saveError: string
  deleteError: string
  deleteConfirm: (title: string) => string
  deleteReason: string
  logView: (title: string) => string
  logCreate: (title: string) => string
  logUpdate: (title: string) => string
  logDelete: (title: string) => string
}

export interface MarketingLinksScreenProps {
  moduleName: string
  kind: MarketingLinkKind
  logEntityType: EntityType
  texts: MarketingLinksScreenTexts
}

const MarketingLinksScreen: React.FC<MarketingLinksScreenProps> = ({
  moduleName,
  kind,
  logEntityType,
  texts,
}) => {
  const { user } = useAuthContext()
  const { logCreate, logUpdate, logDelete, logView } = useUserLogs()
  const { hasPermission: canInsert } = usePermission(moduleName, 'insert')
  const { hasPermission: canUpdate } = usePermission(moduleName, 'update')
  const { hasPermission: canDelete } = usePermission(moduleName, 'delete')

  const [moduleTitle, setModuleTitle] = useState(texts.fallbackTitle)
  const [moduleDescription, setModuleDescription] = useState(texts.fallbackDescription)

  const [links, setLinks] = useState<MarketingLink[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [filters, setFilters] = useState<LinksFilters>(EMPTY_FILTERS)

  const [sortKey, setSortKey] = useState('title')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<MarketingLink | null>(null)
  const [viewing, setViewing] = useState<MarketingLink | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  useEffect(() => {
    modulesService.getByName(moduleName).then((result) => {
      if (result.success && result.data) {
        if (result.data.display_name) setModuleTitle(result.data.display_name)
        if (result.data.description) setModuleDescription(result.data.description)
      }
    })
  }, [moduleName])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await marketingLinksService.list(kind)
      setLinks(list)
    } catch (err) {
      setError(err instanceof Error ? err.message : texts.loadError)
    } finally {
      setLoading(false)
    }
  }, [kind, texts.loadError])

  useEffect(() => {
    void load()
  }, [load])

  const hasActiveFilters = !!filters.search.trim()

  const filtered = useMemo(() => {
    const search = filters.search.trim().toLowerCase()
    if (!search) return links
    return links.filter((row) => {
      const title = row.title.toLowerCase()
      const description = (row.description || '').toLowerCase()
      return title.includes(search) || description.includes(search)
    })
  }, [links, filters])

  const sorted = useMemo(
    () =>
      sortRows(filtered, sortKey, sortOrder, (row, key) => {
        if (key === 'title') return row.title
        if (key === 'description') return row.description || ''
        return ''
      }),
    [filtered, sortKey, sortOrder]
  )

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE) || 1)
  const pageSafe = Math.min(page, totalPages)
  const startIndex = (pageSafe - 1) * PAGE_SIZE
  const endIndex = Math.min(pageSafe * PAGE_SIZE, sorted.length)
  const pageRows = useMemo(
    () => sorted.slice(startIndex, startIndex + PAGE_SIZE),
    [sorted, startIndex]
  )

  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS)
    setPage(1)
  }

  const toggleSort = (key: string) => {
    if (sortKey === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortOrder('asc')
    }
    setPage(1)
  }

  const handlePreviousPage = () => {
    if (pageSafe > 1) setPage(pageSafe - 1)
  }

  const handleNextPage = () => {
    if (pageSafe < totalPages) setPage(pageSafe + 1)
  }

  const openCreate = () => {
    if (!canInsert) return
    setEditing(null)
    setForm(emptyForm())
    setFormError(null)
    setModalOpen(true)
  }

  const openEdit = (row: MarketingLink) => {
    if (!canUpdate) return
    setViewing(null)
    setEditing(row)
    setForm({
      title: row.title,
      description: row.description || '',
      download_url: row.download_url,
    })
    setFormError(null)
    setModalOpen(true)
  }

  const openView = (row: MarketingLink) => {
    setViewing(row)
    void logView(logEntityType, row.id, texts.logView(row.title))
  }

  const closeModal = () => {
    if (saving) return
    setModalOpen(false)
    setEditing(null)
    setFormError(null)
  }

  const handleDownload = (row: MarketingLink) => {
    window.open(row.download_url, '_blank', 'noopener,noreferrer')
  }

  const handleSave = async () => {
    const title = form.title.trim()
    const downloadUrl = form.download_url.trim()
    const description = form.description.trim()

    if (!title) {
      setFormError(texts.titleRequired)
      return
    }
    if (!downloadUrl) {
      setFormError('Informe o link para download.')
      return
    }
    if (!isValidUrl(downloadUrl)) {
      setFormError('Informe um link válido (http ou https).')
      return
    }

    const input: MarketingLinkInput = {
      title,
      description: description || null,
      download_url: downloadUrl,
    }

    setSaving(true)
    setFormError(null)
    try {
      if (editing) {
        const saved = await marketingLinksService.update(editing.id, kind, input)
        void logUpdate(
          logEntityType,
          saved.id,
          texts.logUpdate(saved.title),
          {
            title: editing.title,
            description: editing.description,
            download_url: editing.download_url,
          },
          {
            title: saved.title,
            description: saved.description,
            download_url: saved.download_url,
          }
        )
      } else {
        const created = await marketingLinksService.create(kind, input, user?.id)
        void logCreate(logEntityType, created.id, texts.logCreate(created.title), {
          title: created.title,
          description: created.description,
          download_url: created.download_url,
          created_by: created.created_by,
          kind: created.kind,
        })
      }

      setModalOpen(false)
      setEditing(null)
      await load()
    } catch (err) {
      setFormError(err instanceof Error ? err.message : texts.saveError)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (row: MarketingLink) => {
    if (!canDelete) return
    if (!window.confirm(texts.deleteConfirm(row.title))) return

    try {
      setDeletingId(row.id)
      await marketingLinksService.softDelete(row.id, kind, {
        deleted_by: user?.id,
        deleted_reason: texts.deleteReason,
      })
      void logDelete(logEntityType, row.id, texts.logDelete(row.title), {
        title: row.title,
        description: row.description,
        download_url: row.download_url,
      })
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : texts.deleteError)
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="flex h-full flex-col overflow-x-hidden overflow-y-auto">
      <PermissionGuard moduleName={moduleName} permissionType="show">
        <div className="px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-6 flex items-start justify-between gap-3">
            <div>
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                {moduleTitle}
              </Heading>
              <Text size="sm" color="muted" className="mt-2">
                {moduleDescription}
              </Text>
            </div>
            {canInsert && (
              <PermissionGuard
                moduleName={moduleName}
                permissionType="insert"
                loadingComponent={PERM_LOADING}
                fallback={null}
              >
                <Button variant="solid" onClick={openCreate} className="flex items-center gap-1">
                  <PlusIcon className="h-5 w-5" />
                  {texts.newButton}
                </Button>
              </PermissionGuard>
            )}
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="mb-4 grid grid-cols-1 gap-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:grid-cols-2 lg:grid-cols-3">
            <div className="sm:col-span-1 lg:col-span-2">
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                Buscar
              </label>
              <Input
                value={filters.search}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, search: e.target.value }))
                  setPage(1)
                }}
                placeholder="Título ou descrição"
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                className="flex w-full items-center justify-center gap-1 whitespace-nowrap"
              >
                <XCircleIcon className="h-4 w-4" />
                Limpar filtros
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/30">
                    <tr>
                      <SortableTableHeader
                        sortKey="title"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-4 py-3"
                      >
                        Título
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="description"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-4 py-3"
                      >
                        Descrição
                      </SortableTableHeader>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {pageRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                        >
                          {hasActiveFilters ? texts.emptyFiltered : texts.emptyList}
                        </td>
                      </tr>
                    ) : (
                      pageRows.map((row) => (
                        <tr
                          key={row.id}
                          className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/20"
                          onClick={() => openView(row)}
                        >
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                            {row.title}
                          </td>
                          <td className="max-w-md px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                            <span className="line-clamp-2">{row.description || '—'}</span>
                          </td>
                          <td
                            className="px-4 py-3 text-right"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => handleDownload(row)}
                                className={LINK_ICON_BTN_CLASS}
                                title="Abrir link de download"
                                aria-label="Abrir link de download"
                              >
                                <ArrowTopRightOnSquareIcon className="h-4 w-4" strokeWidth={2} />
                              </button>
                              {canUpdate && (
                                <PermissionGuard
                                  moduleName={moduleName}
                                  permissionType="update"
                                  loadingComponent={PERM_LOADING}
                                  fallback={null}
                                >
                                  <button
                                    type="button"
                                    onClick={() => openEdit(row)}
                                    className={ICON_BTN_CLASS}
                                    title="Editar"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                </PermissionGuard>
                              )}
                              {canDelete && (
                                <PermissionGuard
                                  moduleName={moduleName}
                                  permissionType="delete"
                                  loadingComponent={PERM_LOADING}
                                  fallback={null}
                                >
                                  <button
                                    type="button"
                                    onClick={() => void handleDelete(row)}
                                    disabled={deletingId === row.id}
                                    className={ICON_BTN_DANGER_CLASS}
                                    title="Excluir"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </PermissionGuard>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="border-t border-gray-200 px-6 py-4 dark:border-gray-700">
                <div className="flex items-center justify-between gap-3">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300">
                    {sorted.length === 0
                      ? 'Nenhum registro'
                      : `Mostrando ${startIndex + 1} a ${endIndex} de ${sorted.length} registro${sorted.length !== 1 ? 's' : ''}`}
                  </Text>

                  {totalPages > 1 && (
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={handlePreviousPage}
                        disabled={pageSafe === 1}
                        className={PAGINATION_BTN_CLASS}
                      >
                        Anterior
                      </button>

                      <div className="flex space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNumber: number
                          if (totalPages <= 5) {
                            pageNumber = i + 1
                          } else if (pageSafe <= 3) {
                            pageNumber = i + 1
                          } else if (pageSafe >= totalPages - 2) {
                            pageNumber = totalPages - 4 + i
                          } else {
                            pageNumber = pageSafe - 2 + i
                          }
                          return (
                            <button
                              key={pageNumber}
                              type="button"
                              onClick={() => setPage(pageNumber)}
                              className={`rounded-md px-3 py-2 text-sm font-medium ${
                                pageSafe === pageNumber
                                  ? 'bg-primary-600 text-white'
                                  : 'border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                              }`}
                            >
                              {pageNumber}
                            </button>
                          )
                        })}
                      </div>

                      <button
                        type="button"
                        onClick={handleNextPage}
                        disabled={pageSafe === totalPages}
                        className={PAGINATION_BTN_CLASS}
                      >
                        Próximo
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </PermissionGuard>

      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setViewing(null)} />
          <div className="relative z-10 flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
              <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                {viewing.title}
              </Heading>
              <Button type="button" variant="outline" size="sm" onClick={() => setViewing(null)}>
                Fechar
              </Button>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4 text-sm">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Text size="xs" color="muted">
                    Cadastrado por
                  </Text>
                  <Text className="text-gray-900 dark:text-white">
                    {viewing.created_by_user?.name || '—'}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Data de cadastro
                  </Text>
                  <Text className="text-gray-900 dark:text-white">
                    {formatDateTimePtBr(viewing.created_at)}
                  </Text>
                </div>
                <div className="sm:col-span-2">
                  <Text size="xs" color="muted">
                    Última atualização
                  </Text>
                  <Text className="text-gray-900 dark:text-white">
                    {formatDateTimePtBr(viewing.updated_at)}
                  </Text>
                </div>
                <div className="sm:col-span-2">
                  <Text size="xs" color="muted">
                    Descrição
                  </Text>
                  <Text className="whitespace-pre-wrap text-gray-900 dark:text-white">
                    {viewing.description || '—'}
                  </Text>
                </div>
                <div className="sm:col-span-2">
                  <Text size="xs" color="muted">
                    Link para download
                  </Text>
                  <a
                    href={viewing.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-flex max-w-full items-start gap-1.5 break-all text-primary-600 hover:underline dark:text-primary-400"
                  >
                    <ArrowTopRightOnSquareIcon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} />
                    <span>{viewing.download_url}</span>
                  </a>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleDownload(viewing)}
                className="flex items-center gap-1"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" strokeWidth={2} />
                Abrir link
              </Button>
              {canUpdate && (
                <PermissionGuard
                  moduleName={moduleName}
                  permissionType="update"
                  loadingComponent={PERM_LOADING}
                  fallback={null}
                >
                  <Button
                    type="button"
                    variant="solid"
                    onClick={() => openEdit(viewing)}
                    className="flex items-center gap-1"
                  >
                    <PencilIcon className="h-4 w-4" />
                    Editar
                  </Button>
                </PermissionGuard>
              )}
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={closeModal} />
          <div className="relative z-10 flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
              <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                {editing ? texts.modalEditTitle : texts.modalCreateTitle}
              </Heading>
              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
              {formError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                  {formError}
                </div>
              )}

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Título
                </label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  placeholder={texts.titlePlaceholder}
                  disabled={saving}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Descrição
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  rows={3}
                  className={TEXTAREA_CLASS}
                  placeholder={texts.descriptionPlaceholder}
                  disabled={saving}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Link para download
                </label>
                <Input
                  value={form.download_url}
                  onChange={(e) => setForm((p) => ({ ...p, download_url: e.target.value }))}
                  placeholder="https://..."
                  disabled={saving}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
              <Button variant="outline" onClick={closeModal} disabled={saving}>
                Cancelar
              </Button>
              <Button
                variant="solid"
                onClick={() => void handleSave()}
                disabled={saving}
                className="flex items-center gap-1"
              >
                <CheckIcon className="h-4 w-4" />
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MarketingLinksScreen
